import React from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledTypography } from '../styles/styles';

const defectData = [
  { score: 7, defect: 'Particle contamination', cause: 'Foreign particles (e.g., metal flakes) during electrode coating', risk: 'Internal short circuits, fire risk' },
  { score: 5, defect: 'Electrode misalignment', cause: 'Poor layer stacking or winding in cylindrical/pouch cells', risk: 'Non-uniform current distribution' },
  { score: 4, defect: 'Welding defects', cause: 'Poor tabs/cell terminal welds', risk: 'High contact resistance, overheating' },
  { score: 3, defect: 'Delamination', cause: 'Poor adhesion between electrode and current collector', risk: 'Increased impedance, capacity fade' },
  { score: 4, defect: 'Electrolyte leakage / Dry spots', cause: 'Poor sealing or incomplete electrolyte wetting', risk: 'Ionic conduction loss, swelling' },
  { score: 5, defect: 'Self-discharge rate', cause: 'Internal leakage paths, micro-shorts, or SEI defects', risk: 'Energy loss, reduced calendar life' },
  { score: 7, defect: 'Internal short circuit', cause: 'Dendrites, metallic particles, separator failure', risk: 'Thermal runaway, catastrophic failure' },
  { score: 6, defect: 'Lithium plating', cause: 'Fast charging at low temperatures or high SoC', risk: 'Dendrite growth, internal short risk' },
  { score: 5, defect: 'SEI layer instability', cause: 'Poor formation cycles, mechanical stress, or high-temperature cycling', risk: 'Capacity fade, impedance rise' },
  { score: 6, defect: 'Internal structural deformation', cause: 'Pressure, impact, or gas buildup causing layer shifting or crushing', risk: 'Reduced capacity, short risk, safety hazard' },
];

const DefectAnalysis = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%)',
      borderRadius: '2rem',
      p: 3,
      fontFamily: 'Bai Jamjuree',
      color: '#fff',
      height: '90vh',
      position: 'relative',
      border: '1px solid rgba(232, 68, 45, 0.2)',
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Side Label */}
      <Box sx={{
        writingMode: 'vertical-rl',
        textOrientation: 'mixed',
        color: '#e8442d',
        fontWeight: 900,
        fontSize: '1.3rem',
        letterSpacing: 2,
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        justifyContent: 'center',
        mr: 3,
        ml: 1,
        textShadow: '0 0 8px #000',
      }}>
        Defect Analysis
      </Box>
      {/* Main Content Scrollable */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '90vh', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 4, width: '100%' }}>
          {/* Overall Risk Score */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
            <StyledTypography variant="h5" sx={{ color: '#e8442d', fontWeight: 900, mb: 1, letterSpacing: 1 }}>
              Overall Risk Score
            </StyledTypography>
            <Box sx={{ display: 'inline-block', px: 4, py: 1, borderRadius: '1.5rem', background: 'rgba(232,68,45,0.12)', border: '2px solid #e8442d', fontWeight: 900, color: '#fff', fontSize: '2rem', letterSpacing: 2, mt: 1 }}>
              4/10
            </Box>
          </Box>
          {/* Table */}
          <TableContainer component={Paper} sx={{ background: 'linear-gradient(135deg, #181818, #232323)', borderRadius: '1rem', border: '2px solid #e8442d', boxShadow: '0 4px 16px rgba(232,68,45,0.08)', color: '#fff', maxWidth: '100%', overflowX: 'auto', mb: 4, ml: 0, fontFamily: 'Bai Jamjuree' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ color: '#e8442d', fontWeight: 900, fontSize: '1.1rem', borderBottom: '2px solid #e8442d', background: '#181818', position: 'sticky', top: 0, zIndex: 1, fontFamily: 'Bai Jamjuree' }}>Risk Score (1–10)</TableCell>
                  <TableCell align="left" sx={{ color: '#e8442d', fontWeight: 900, fontSize: '1.1rem', borderBottom: '2px solid #e8442d', background: '#181818', position: 'sticky', top: 0, zIndex: 1, fontFamily: 'Bai Jamjuree' }}>Defect</TableCell>
                  <TableCell align="left" sx={{ color: '#e8442d', fontWeight: 900, fontSize: '1.1rem', borderBottom: '2px solid #e8442d', background: '#181818', position: 'sticky', top: 0, zIndex: 1, fontFamily: 'Bai Jamjuree' }}>Cause</TableCell>
                  <TableCell align="left" sx={{ color: '#e8442d', fontWeight: 900, fontSize: '1.1rem', borderBottom: '2px solid #e8442d', background: '#181818', position: 'sticky', top: 0, zIndex: 1, fontFamily: 'Bai Jamjuree' }}>Risk</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defectData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      background: idx % 2 === 0 ? 'rgba(232,68,45,0.06)' : 'transparent',
                      transition: 'background 0.2s',
                      '&:hover': {
                        background: 'rgba(232,68,45,0.18)',
                      },
                    }}
                  >
                    <TableCell align="center" sx={{ color: '#fff', fontWeight: 700, borderBottom: '1px solid #333', background: 'transparent', py: 2, px: 1, fontFamily: 'Bai Jamjuree' }}>{row.score}</TableCell>
                    <TableCell align="left" sx={{ color: '#fff', fontWeight: 700, borderBottom: '1px solid #333', background: 'transparent', py: 2, px: 1, fontFamily: 'Bai Jamjuree' }}>{row.defect}</TableCell>
                    <TableCell align="left" sx={{ color: '#fff', fontWeight: 700, borderBottom: '1px solid #333', background: 'transparent', py: 2, px: 1, fontFamily: 'Bai Jamjuree' }}>{row.cause}</TableCell>
                    <TableCell align="left" sx={{ color: '#fff', fontWeight: 700, borderBottom: '1px solid #333', background: 'transparent', py: 2, px: 1, fontFamily: 'Bai Jamjuree' }}>{row.risk}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {/* Navigation Buttons */}
        <Box sx={{
          position: 'fixed',
          left: 0,
          bottom: 0,
          width: '100vw',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          pointerEvents: 'none',
          zIndex: 1200,
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBack />}
            sx={{
              background: 'linear-gradient(45deg, #e8442d, #ff6b35)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              borderRadius: '25px',
              px: 4,
              py: 1.5,
              boxShadow: '0 8px 25px rgba(232, 68, 45, 0.4)',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
              pointerEvents: 'auto',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(232, 68, 45, 0.6)',
              },
            }}
            onClick={() => navigate(-1)}
          >
            Prev
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForward />}
            sx={{
              background: 'linear-gradient(45deg, #e8442d, #ff6b35)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              borderRadius: '25px',
              px: 4,
              py: 1.5,
              boxShadow: '0 8px 25px rgba(232, 68, 45, 0.4)',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
              pointerEvents: 'auto',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(232, 68, 45, 0.6)',
              },
            }}
            onClick={() => navigate('/batteryscope-c/cell-health-certificate')}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DefectAnalysis;
